//AppLayout.jsx

import React,{useEffect,useState} from "react";
import TopTabs from "../ui/TopTabs";
import HistoryPanel from "../ui/HistoryPanel";
import Loader from "../ui/Loader";
import RepoInput from "../components/RepoInput";
import LogoutButton from "../components/LogoutButton";
import ArchitectureGraph from "../report/ArchitectureGraph";
import CodebaseMap from "../report/CodebaseMap";
import {useJobProgress} from "../hooks/useJobProgress";
import {useReportCore} from "../hooks/useReportCore";
import {useMap} from "../hooks/useMap";
import {useHeat} from "../hooks/useHeat";
import ArchitectureDashboard from "../report/ArchitectureDashboard";
import {useCurrentJob} from "../hooks/useCurrentJob";
import {useNavigate} from "react-router-dom";
import {fetchCurrentUser,getCurrentUser} from "../api/reportApi";
import AgentPanel from "../ui/AgentPanel";

const FE_DEBUG= false;
const FINISHED_STATUSES=["DONE","ANALYSIS_DONE","COMPLETED","FAILED","NOT_FOUND"];

export default function AppLayout({initialJobId=null}){
    const [activeTab,setActiveTab]=useState("overview");
    const [user,setUser]=useState(()=>getCurrentUser());
    const navigate=useNavigate();
    const [userLoaded,setUserLoaded]=useState(false);
    const {jobId,setCurrentJob,clearJob}=useCurrentJob();

    useEffect(()=>{
        if(initialJobId)setCurrentJob(initialJobId);
    },[initialJobId,setCurrentJob]);

    useEffect(()=>{
        fetchCurrentUser()
            .then(data=>setUser(data))
            .catch(e=>{
                console.warn("Failed to load current user",e);
                setUser(null);
            })
            .finally(()=>setUserLoaded(true));
    },[]);

    const handleStarted=newJobId=>{
        setCurrentJob(newJobId);
        setActiveTab("overview");
    };

    const handleHistorySelect=selectedJobId=>{
        console.log("HISTORY SELECT:",selectedJobId);
        setCurrentJob(selectedJobId);
        setActiveTab("overview");
    };

    const {progress,status,timestamp,error,errorCode}=useJobProgress(jobId);
    const isFinished=FINISHED_STATUSES.includes(status);
    const core=useReportCore(jobId,status);
    const graph={data:core.data?.graph??null,isLoading:false,error:null};
    const map=useMap(jobId,activeTab==="hotspots");
    const heat=useHeat(jobId,activeTab==="architecture");

    const debugState = {
        time: new Date().toISOString(),

        job: {
            jobId: jobId ?? null,
            storedJobId: localStorage.getItem("custor.currentJob"),
            status: status ?? null,
            progress: progress ?? null,
            timestamp: timestamp ?? null,
            error: error ?? null,
            errorCode: errorCode ?? null,
            activeTab: activeTab ?? null
        },

        user: {
            email: user?.email ?? null,
            plan: user?.plan ?? null
        },

        dashboard: {
            status: core.status ?? null,
            fetchStatus: core.fetchStatus ?? null,
            loading: !!core.isLoading,
            fetching: !!core.isFetching,
            error: core.error?.message ?? null,
            hasData: !!core.data,
            type: core.data === null ? "null" : Array.isArray(core.data) ? "array" : typeof core.data,
            keys: core.data ? Object.keys(core.data) : [],

            summary: {
                exists: !!core.data?.summary,
                type: core.data?.summary === null
                    ? "null"
                    : Array.isArray(core.data?.summary)
                        ? "array"
                        : typeof core.data?.summary,
                keys: core.data?.summary ? Object.keys(core.data.summary) : [],
                reportKeys: core.data?.summary?.report
                    ? Object.keys(core.data.summary.report)
                    : [],
                dataKeys: core.data?.summary?.data
                    ? Object.keys(core.data.summary.data)
                    : [],
                hasRepositoryRisk: core.data?.summary?.repositoryRisk != null,
                hasOverallScore: core.data?.summary?.overallScore != null,
                hasTopProblems: Array.isArray(core.data?.summary?.topProblems),
                topProblemsCount: Array.isArray(core.data?.summary?.topProblems)
                    ? core.data.summary.topProblems.length
                    : null,
                hasNestedData: !!core.data?.summary?.data,
                nestedDataKeys: core.data?.summary?.data
                    ? Object.keys(core.data.summary.data)
                    : []
            },

            regions: {
                exists: !!core.data?.regions,
                type: core.data?.regions === null
                    ? "null"
                    : Array.isArray(core.data?.regions)
                        ? "array"
                        : typeof core.data?.regions,
                keys: core.data?.regions ? Object.keys(core.data.regions) : [],
                count: Array.isArray(core.data?.regions)
                    ? core.data.regions.length
                    : null
            },

            worstFiles: {
                exists: !!core.data?.worstFiles,
                keys: core.data?.worstFiles
                    ? Object.keys(core.data.worstFiles)
                    : [],
                repositoryFilesCount: Array.isArray(
                    core.data?.worstFiles?.repositoryFiles
                )
                    ? core.data.worstFiles.repositoryFiles.length
                    : null
            },

            graph: {
                exists: !!core.data?.graph,
                type: core.data?.graph === null
                    ? "null"
                    : Array.isArray(core.data?.graph)
                        ? "array"
                        : typeof core.data?.graph,
                keys: core.data?.graph
                    ? Object.keys(core.data.graph)
                    : [],
                nodesCount: Array.isArray(core.data?.graph?.nodes)
                    ? core.data.graph.nodes.length
                    : null,
                linksCount: Array.isArray(core.data?.graph?.links)
                    ? core.data.graph.links.length
                    : null
            },

            advice: {
                exists: !!core.data?.advice,
                type: core.data?.advice === null
                    ? "null"
                    : Array.isArray(core.data?.advice)
                        ? "array"
                        : typeof core.data?.advice,
                keys: core.data?.advice
                    ? Object.keys(core.data.advice)
                    : [],
                count: Array.isArray(core.data?.advice)
                    ? core.data.advice.length
                    : null
            }
        },

        hotspots: {
            enabled: activeTab === "hotspots",
            loading: !!map.isLoading,
            fetching: !!map.isFetching,
            error: map.error?.message ?? null,
            hasData: !!map.data,
            type: map.data === null
                ? "null"
                : Array.isArray(map.data)
                    ? "array"
                    : typeof map.data,
            keys: map.data && !Array.isArray(map.data)
                ? Object.keys(map.data)
                : [],
            count: Array.isArray(map.data)
                ? map.data.length
                : null
        },

        architecture: {
            enabled: activeTab === "architecture",
            loading: !!heat.isLoading,
            fetching: !!heat.isFetching,
            error: heat.error?.message ?? null,
            hasData: !!heat.data,
            type: heat.data === null
                ? "null"
                : Array.isArray(heat.data)
                    ? "array"
                    : typeof heat.data,
            keys: heat.data && !Array.isArray(heat.data)
                ? Object.keys(heat.data)
                : [],
            nodesCount: Array.isArray(heat.data?.nodes)
                ? heat.data.nodes.length
                : null,
            linksCount: Array.isArray(heat.data?.links)
                ? heat.data.links.length
                : null,
            itemsCount: Array.isArray(heat.data)
                ? heat.data.length
                : null
        },

        graphTab: {
            exists: !!graph.data,
            type: graph.data === null
                ? "null"
                : Array.isArray(graph.data)
                    ? "array"
                    : typeof graph.data,
            keys: graph.data
                ? Object.keys(graph.data)
                : [],
            nodesCount: Array.isArray(graph.data?.nodes)
                ? graph.data.nodes.length
                : null,
            linksCount: Array.isArray(graph.data?.links)
                ? graph.data.links.length
                : null
        },

        api: {
            statusUrl: jobId
                ? `/jobs/${jobId}/status`
                : null,

            dashboardUrl: jobId
                ? `/repo/${jobId}/dashboard`
                : null,

            hotspotsEnabled: activeTab === "hotspots",
            architectureEnabled: activeTab === "architecture"
        }
    };

    const copyDebug = async () => {
        const text = JSON.stringify(debugState, null, 2);

        try {
            await navigator.clipboard.writeText(text);
            alert("FE DEBUG copied");
        } catch {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            textarea.remove();
            alert("FE DEBUG copied");
        }
    };

    const renderContent=()=>{
        if(!jobId){
            return(
                <div className="custor-welcome">
                    <h1>Custor - From Code to Clarity</h1>
                    <p>Select report or start analysis</p>
                    <RepoInput onStarted={handleStarted}/>
                </div>
            );
        }

        if(status==="FAILED"||status==="ERROR"){
            return(
                <div className="custor-welcome">
                    <h1>Custor - Architecture intelligence system</h1>
                    <div className="custor-error">
                        {errorCode==="PLAN_LIMIT_EXCEEDED"?(
                            <>
                                <h2>Upgrade required</h2>
                                <p>Your current plan does not support repositories of this size.</p>
                                <p>Please upgrade your plan to continue the analysis.</p>
                            </>
                        ):(
                            <>
                                <h2>Analysis failed</h2>
                                <p>{error}</p>
                            </>
                        )}
                    </div>
                    <RepoInput onStarted={newJobId=>{
                        clearJob();
                        handleStarted(newJobId);
                    }}/>
                </div>
            );
        }

        if(!isFinished&&status!=="NOT_FOUND"&&status!=="WAITING")
            return <Loader progress={progress}/>;

        if(core.isLoading)
            return <Loader progress={100}/>;

        if(core.error)
            return <div>Failed to load report</div>;

        if(!core.data)
            return <div>No data</div>;

        switch(activeTab){
            case "overview":
                return(
                    <ArchitectureDashboard
                        analysis={core.data?.summary?.data}
                        repo={core.data?.summary?.repo}
                        advice={core.data?.advice?.data}
                    />
                );

            case "hotspots":
                if(map.isLoading)return <Loader/>;
                return(
                    <section style={{padding:"24px"}}>
                        <h1 style={{paddingLeft:"255px"}}>Code Hotspots</h1>
                        <CodebaseMap files={map.data||[]}/>
                    </section>
                );

            case "architecture":
                if(graph.isLoading||heat.isLoading)return <Loader/>;
                return(
                    <section style={{padding:"24px"}}>
                        <h1 style={{paddingLeft:"255px"}}>Architecture Graph</h1>
                        <ArchitectureGraph graph={graph.data}/>
                    </section>
                );

            case "agent":
                return <AgentPanel jobId={jobId} user={user}/>;

            default:
                return null;
        }
    };

    return(
        <div className="custor-root">
            {FE_DEBUG&&(
                <button
                    onClick={copyDebug}
                    style={{
                        position:"fixed",
                        bottom:"10px",
                        left:"10px",
                        zIndex:99999,
                        padding:"7px 10px",
                        fontSize:"11px",
                        fontWeight:700,
                        cursor:"pointer"
                    }}
                >
                    COPY FE DEBUG
                </button>
            )}

            <header className="custor-header">
                <div
                    className="custor-title"
                    onClick={()=>{
                        clearJob();
                        setActiveTab("overview");
                    }}
                >
                    Custor
                </div>

                <TopTabs
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    jobId={jobId}
                />

                <div className="custor-header-actions">
                    <div className="custor-account">
                        {/*<span className="custor-user">
                            {userLoaded ? (user?.role || "UNKNOWN") : "..."}
                        </span>*/}
                        <span className="custor-plan">
                            <strong>Plan:</strong>{" "}
                            {userLoaded?(user?.plan||"UNKNOWN"):"..."}
                        </span>
                    </div>

                    <button
                        className="custor-manage-plan"
                        onClick={()=>navigate("/app/plan")}
                    >
                        Manage plan
                    </button>

                    {user?.role === "ADMIN" && (
                        <button
                            className="custor-manage-plan"
                            onClick={() => navigate("/admin/billing-provider")}
                        >
                            Billing provider
                        </button>
                    )}

                    <LogoutButton/>
                </div>
            </header>

            <div className="custor-body">
                <HistoryPanel
                    onSelectJob={handleHistorySelect}
                    setLoading={()=>{}}
                    refreshKey={`${jobId}:${status}`}
                    user={user}
                    collapsible={activeTab === "architecture"}
                />

                <main className="custor-content">
                    {jobId&&(
                        <section style={{marginBottom:"10px"}}>
                            <strong>Status:</strong> {status}{" | "}
                            <strong>Progress:</strong> {Math.round(progress)}%{" | "}
                            <strong>Timestamp:</strong>{" "}
                            {timestamp?new Date(timestamp).toLocaleTimeString():"-"}
                        </section>
                    )}

                    {renderContent()}
                </main>
            </div>
        </div>
    );
}
